import apiV2 from '../api/v2';

// Mirrors dashboard/src/services/patient/internalImages.ts (commit 6fbd4bf9),
// scoped to /practices/:practiceId instead of /patients/:patientId, with two
// deliberate differences from that file:
//   - `response.data.data`, not `response.data` - dashboard's apiV2 has a
//     response interceptor that unwraps the {success, data} envelope;
//     files-viewer's apiV2 (src/api/v2.ts) is a plain passthrough, matching
//     every other files-viewer service (see services/communications.ts).
//   - No client-side width/height measurement before upload - our upload
//     response contract is just {url, fileId, objectPath} and nothing here
//     (or the shared RichEditor) reads image dimensions, so that step would
//     be unused complexity.
// Only one context exists server-side today (PRACTICE_IMAGE_CONTEXTS), so
// this only accepts 'call' rather than a wider union - widen it here the day
// a second practice-scoped consumer shows up. The literal stays 'call' (not
// 'communication') because it names the upload context, unchanged by the
// Calls -> Communications rework.
export type PracticeImageContext = 'call';

export interface PracticeImageUploadResponse {
    url: string;
    fileId: string;
    objectPath: string;
}

const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/**
 * Uploads an inline image embedded in a practice-scoped rich-text body (the
 * communications feature's only consumer so far) and returns its signed GCS URL
 * (usable immediately, but expires - never persisted) plus the `fileId`
 * (the one part of this that's safe to store long-term).
 *
 * Throws on validation failure or network error so the caller can show it
 * through the editor's own upload-error UI.
 */
export async function uploadPracticeImage(
    practiceId: string,
    file: File,
    context: PracticeImageContext
): Promise<PracticeImageUploadResponse> {
    if (!ALLOWED_MIME.has(file.type)) {
        throw new Error('Image must be JPEG, PNG, or WebP.');
    }
    if (file.size > MAX_UPLOAD_BYTES) {
        throw new Error('Image exceeds the 8MB size limit.');
    }

    const form = new FormData();
    form.append('image', file, file.name);
    form.append('context', context);

    const response = await apiV2.post<{ success: true; data: PracticeImageUploadResponse }>(
        `/practices/${encodeURIComponent(practiceId)}/internal/images`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return response.data.data;
}

/**
 * Resolves practice-scoped image fileIds to fresh signed URLs. Omits any
 * fileId the backend can't/won't resolve (foreign or unknown ids are left
 * out of the map silently, not treated as errors) so callers can degrade
 * gracefully: text stays intact, that one image renders broken.
 *
 * Throws on network/HTTP failure - this does not swallow those into an empty
 * map itself; the sole caller (useCallForm's resolve-on-load effect) catches
 * the rejection and leaves the stale doc in place rather than surfacing it.
 */
export async function resolvePracticeImageUrls(
    practiceId: string,
    fileIds: string[]
): Promise<Record<string, string>> {
    if (fileIds.length === 0) return {};
    const response = await apiV2.post<{ success: true; data: { urls: Record<string, string> } }>(
        `/practices/${encodeURIComponent(practiceId)}/internal/images/resolve`,
        { fileIds }
    );
    return response.data?.data?.urls ?? {};
}
